import { Party } from "../enums/Party";
import { FormikStepper } from "../navigation/FormikStepper";
import { FormikStep } from "../navigation/FormikStep";
import { EmployeePages } from "../enums/EmployeePages";
import EmployeeStart from "../page/employee/EmployeeStart";
import { object, string } from "yup";
import {
    accountNumberValidator,
    requiredIfFieldNotPresent,
    validateDocumentContent,
    validateDocumentsTotalSize,
    validateFieldNotEqualTo,
    validateHasRequiredDocuments,
    validateInfertility,
    validatePolicyNumber,
    validateTwoFieldsNotEqualTo,
    validRoutingNumber
} from "../../form/Validators";
import AboutYou from "../page/employee/AboutYou";
import ContactYou from "../page/employee/ContactYou";
import Employer from "../page/employee/Employer";
import ClaimDates from "../page/employee/ClaimDates";
import ClaimType from "../page/employee/EmployeeClaimType";
import EmployeeDocument from "../page/employee/EmployeeDocument";
import Benefits from "../page/employee/Benefits";
import Payments from "../page/common/Payments";
import EmployeeSummary from "../page/employee/EmployeeSummary";
import EmployeeHipaa from "../page/employee/EmployeeHipaa";
import React from "react";
import { gatherInitialValues, submitHandler } from "../../service/FormService";
import { BenefitType, findRelationshipsFor } from "../enums/BenefitType";
import EmployeeFinished from "../page/employee/EmployeeFinished";
import AboutRelationship from "../page/common/AboutRelationship";
import { useParams } from "react-router-dom";
import { FormValues } from "./FormValues";
import TreatingPhysicianInformation from "../page/employee/TreatingPhysicianInformation";
import ChildSupport from "../page/employee/ChildSupport";

const Employee = () => {
    const params = useParams();
    const coverage = params.coverage as BenefitType;
    const claimId = params.claimId;

    return <FormikStepper initialValues={gatherInitialValues(claimId, coverage, Party.EMPLOYEE)}
                          onSubmit={submitHandler}>
        <FormikStep label={EmployeePages.START}>
            <EmployeeStart/>
        </FormikStep>
        <FormikStep label={EmployeePages.YOUR_INFO} hideStep={(initialValues) => initialValues.firstName === undefined}>
            <AboutYou/>
        </FormikStep>
        <FormikStep label={EmployeePages.CONTACT_INFO}
                    hideStep={(initialValues) => initialValues.phoneNumber === undefined}>
            <ContactYou/>
        </FormikStep>
        <FormikStep label={EmployeePages.EMPLOYER}
                    hideStep={(initialValues) => initialValues.policyNumber === undefined}
                    validationSchema={object({
                        policyNumber: validatePolicyNumber(),
                        employerName:
                            requiredIfFieldNotPresent('policyNumber', 'If you don\'t specify your employer\'s group number, please specify your employer\'s name.')
                                .concat(validateTwoFieldsNotEqualTo('firstName', 'lastName', "Your employer's details must not be the same as your personal details.")),
                        employerZipCode: requiredIfFieldNotPresent('policyNumber', 'If you don\'t specify your employer\'s group number, please specify your employer\'s ZIP code.'),
                        employerEmailAddress:
                            requiredIfFieldNotPresent('policyNumber', 'If you don\'t specify your employer\'s group number, please specify your employer\'s email address.')
                                .concat(validateFieldNotEqualTo('emailAddress', "Your employer's details must not be the same as your personal details."))
                                .concat(string().email("Please specify a valid email address."))
                    })}>
            <Employer/>
        </FormikStep>
        <FormikStep label={EmployeePages.RELATIONSHIP} hideStep={(_, values) => findRelationshipsFor(values as FormValues).length < 2}>
            <AboutRelationship/>
        </FormikStep>
        <FormikStep label={EmployeePages.CHILD_SUPPORT} hideStep={(_, values) => values.workState !== "MN"}>
            <ChildSupport/>
        </FormikStep>
        <FormikStep label={EmployeePages.CLAIM_DATES} hideStep={(initialValues) => initialValues.claimDates === undefined}>
            <ClaimDates/>
        </FormikStep>
        <FormikStep label={EmployeePages.CLAIM_TYPE} hideStep={(initialValues) =>
            initialValues.leaveType === undefined
            && initialValues.conditionType === undefined
            && initialValues.screeningType === undefined}
            validate={(values) => {
                return {
                    ...validateInfertility(values),
                }
            }}>
            <ClaimType/>
        </FormikStep>
        <FormikStep label={EmployeePages.TREATING_PHYSICIAN} hideStep={(initialValues, values) =>
            initialValues.facilityName === undefined
            && (initialValues.treatingPhysicians === undefined || !values.medicalRelated)}>
            <TreatingPhysicianInformation/>
        </FormikStep>
        <FormikStep label={EmployeePages.BENEFITS}
                    hideStep={(initialValues) => initialValues.hospitalization === undefined && initialValues.otherBenefits === undefined}>
            <Benefits/>
        </FormikStep>
        <FormikStep label={EmployeePages.DOCUMENT}
                    hideStep={(initialValues) => initialValues.documents === undefined}
                    validationSchema={object({
                        documents: validateDocumentContent()
                    })}
                    validate={(values) => {
                        return {
                            ...validateDocumentsTotalSize(values),
                            ...validateHasRequiredDocuments(values)
                        }
                    }}>
            <EmployeeDocument/>
        </FormikStep>
        <FormikStep label={EmployeePages.PAYMENTS}
                    hideStep={(initialValues) => initialValues.paymentPreference === undefined}
                    validationSchema={object({
                        routingNumber: validRoutingNumber(Party.EMPLOYEE),
                        accountNumber2: accountNumberValidator(Party.EMPLOYEE)
                    })}>
            <Payments/>
        </FormikStep>
        <FormikStep label={EmployeePages.HIPAA} hideStep={(initialValues) =>
            initialValues.hipaaConsent === undefined
        }>
            <EmployeeHipaa/>
        </FormikStep>
        <FormikStep label={EmployeePages.SUMMARY}>
            <EmployeeSummary/>
        </FormikStep>
        <FormikStep label={EmployeePages.FINISH}>
            <EmployeeFinished/>
        </FormikStep>
    </FormikStepper>
}

export default Employee;